import type { User } from "../services/api";

export const EXPLORER_TAB_PRODUCT_IDS: Record<number, string> = {
  0: "explorer-programs",
  2: "explorer-sponsors",
  3: "explorer-livestreams",
  4: "explorer-compare",
};

const EXPLORER_GRANULAR_TAB_IDS: readonly string[] =
  Object.values(EXPLORER_TAB_PRODUCT_IDS);

/**
 * When per-user mode is on but userProducts is empty, inherit company products (matches backend).
 * If the user still has the umbrella `explorer` id but the company only enables granular tab
 * products, expand to those company-offered tab ids so access matches intent.
 */
export function resolveEffectiveUserProducts(
  perUser: boolean,
  userProducts: string[] | undefined,
  companyProductsList: string[],
): string[] {
  if (!perUser) return companyProductsList;
  const up = userProducts || [];
  if (up.length === 0) return companyProductsList;
  const filtered = up.filter((p) => companyProductsList.includes(p));
  if (
    up.includes("explorer") &&
    !companyProductsList.includes("explorer")
  ) {
    const fromCompany = EXPLORER_GRANULAR_TAB_IDS.filter((id) =>
      companyProductsList.includes(id),
    );
    const merged = filtered.concat(fromCompany);
    const seen = new Set<string>();
    const deduped: string[] = [];
    for (const id of merged) {
      if (!seen.has(id)) {
        seen.add(id);
        deduped.push(id);
      }
    }
    return deduped;
  }
  return filtered;
}

export function getDefaultRouteForRoleAndProducts(
  userRole: string | undefined,
  products: string[],
): string {
  // Same access as the Chat sidebar item and ChatPage guard.
  if (
    userRole === "admin" ||
    products.includes("mispodcasts") ||
    products.includes("micontenido") ||
    products.includes("explorer") ||
    products.includes(EXPLORER_TAB_PRODUCT_IDS[2]!)
  ) {
    return "/chat";
  }
  if (Object.values(EXPLORER_TAB_PRODUCT_IDS).some((p) => products.includes(p))) {
    return "/explorador/panel";
  }
  return "/auth";
}

/** Explorer Clips tab index (`EXPLORER_SOCIAL_TAB`). Granted only by the `clips` product. */
const EXPLORER_CLIPS_TAB = 6;

export function userHasProductAccess(
  user: User | null | undefined,
  accessibleProducts: string[],
  product: string,
): boolean {
  // Clips follows the company switch for every role, including platform admins.
  if (product === "clips") return accessibleProducts.includes("clips");
  if (user?.role === "admin") return true;
  return accessibleProducts.includes(product);
}

export function userHasExplorerTabAccess(
  user: User | null | undefined,
  accessibleProducts: string[],
  tabIndex: number,
): boolean {
  if (tabIndex === EXPLORER_CLIPS_TAB) {
    return accessibleProducts.includes("clips");
  }
  if (user?.role === "admin") return true;
  if (accessibleProducts.includes("explorer")) return true;
  const tabProduct = EXPLORER_TAB_PRODUCT_IDS[tabIndex];
  return tabProduct ? accessibleProducts.includes(tabProduct) : false;
}

export function userHasAnyExplorerAccess(
  user: User | null | undefined,
  accessibleProducts: string[],
): boolean {
  if (user?.role === "admin") return true;
  if (accessibleProducts.includes("explorer")) return true;
  return Object.values(EXPLORER_TAB_PRODUCT_IDS).some((p) =>
    accessibleProducts.includes(p),
  );
}
